import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';

import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import { getReportedPosts } from '@/shared/services/reportApi';
import SEO from '@/components/common/SEO';
import './AdminDashboard.css';

// TODO: Replace with real monthly growth data from a dedicated endpoint
const MOCK_MONTHLY_DATA = [
    { month: 'Jan', users: 920 },
    { month: 'Feb', users: 970 },
    { month: 'Mar', users: 1010 },
    { month: 'Apr', users: 1040 },
    { month: 'May', users: 1075 },
    { month: 'Jun', users: 1110 },
    { month: 'Jul', users: 1135 },
    { month: 'Aug', users: 1180 },
    { month: 'Sep', users: 1190 },
    { month: 'Oct', users: 1200 },
    { month: 'Nov', users: 1215 },
    { month: 'Dec', users: 1234 },
];

// --- SVG Line Chart ---
function UserGrowthChart({ data }) {
    const [tooltip, setTooltip] = useState(null);

    const W = 560,
        H = 260;
    const PAD = { top: 20, right: 20, bottom: 44, left: 52 };
    const cW = W - PAD.left - PAD.right;
    const cH = H - PAD.top - PAD.bottom;
    const maxY = 1400;
    const yTicks = [0, 350, 700, 1050, 1400];

    const getX = (i) => PAD.left + (i / (data.length - 1)) * cW;
    const getY = (v) => PAD.top + (1 - v / maxY) * cH;

    const polylinePoints = data
        .map((d, i) => `${getX(i)},${getY(d.users)}`)
        .join(' ');

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            role="group"
            aria-label="User growth line chart showing monthly user counts"
            className="w-100"
        >
            {/* Grid lines */}
            {yTicks.map((tick) => (
                <g key={tick}>
                    <line
                        x1={PAD.left}
                        y1={getY(tick)}
                        x2={W - PAD.right}
                        y2={getY(tick)}
                        stroke="var(--bs-border-color)"
                        strokeDasharray="4 4"
                        strokeWidth="1"
                    />
                    <text
                        x={PAD.left - 6}
                        y={getY(tick)}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize="16"
                        fill="var(--bs-secondary-color)"
                    >
                        {tick}
                    </text>
                </g>
            ))}

            {/* X axis labels */}
            {data.map((d, i) => (
                <text
                    key={d.month}
                    x={getX(i)}
                    y={H - PAD.bottom + 16}
                    textAnchor="middle"
                    fontSize="16"
                    fill="var(--bs-secondary-color)"
                >
                    {d.month}
                </text>
            ))}

            {/* Line */}
            <polyline
                points={polylinePoints}
                fill="none"
                stroke="var(--bs-primary)"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
            />

            {/* Data points + hover targets */}
            {data.map((d, i) => (
                <g key={d.month}>
                    <circle
                        cx={getX(i)}
                        cy={getY(d.users)}
                        r="4"
                        fill="var(--bs-primary)"
                    />
                    {/* Invisible larger hit target. Keyboard-accessible:
                        tabIndex makes the SVG node focusable, focus/blur
                        mirror the hover handlers so the tooltip is reachable
                        without a mouse, and aria-label announces the data
                        point to assistive tech. */}
                    <circle
                        cx={getX(i)}
                        cy={getY(d.users)}
                        r="12"
                        fill="transparent"
                        tabIndex={0}
                        role="button"
                        aria-label={`${d.month}: ${d.users.toLocaleString()} users`}
                        onMouseEnter={() =>
                            setTooltip({ i, d, x: getX(i), y: getY(d.users) })
                        }
                        onMouseLeave={() => setTooltip(null)}
                        onFocus={() =>
                            setTooltip({ i, d, x: getX(i), y: getY(d.users) })
                        }
                        onBlur={() => setTooltip(null)}
                        className="admin-chart-point-target"
                    />
                </g>
            ))}

            {/* Tooltip */}
            {tooltip && (
                <g>
                    <rect
                        x={tooltip.x - 60}
                        y={tooltip.y - 56}
                        width="120"
                        height="48"
                        rx="4"
                        className="admin-chart-tooltip-bg"
                        stroke="var(--bs-border-color)"
                        strokeWidth="1"
                    />
                    <text
                        x={tooltip.x}
                        y={tooltip.y - 36}
                        textAnchor="middle"
                        fontSize="16"
                        fill="var(--bs-body-color)"
                        fontWeight="600"
                    >
                        {tooltip.d.month}
                    </text>
                    <text
                        x={tooltip.x}
                        y={tooltip.y - 16}
                        textAnchor="middle"
                        fontSize="16"
                        fill="var(--bs-secondary-color)"
                    >
                        {tooltip.d.users.toLocaleString()} users
                    </text>
                </g>
            )}
        </svg>
    );
}

UserGrowthChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            month: PropTypes.string.isRequired,
            users: PropTypes.number.isRequired,
        })
    ).isRequired,
};

// --- SVG Pie Chart ---
function RoleDistributionChart({ roleCounts, total }) {
    const cx = 120,
        cy = 130,
        r = 100;

    const roles = [
        {
            label: 'User',
            count: roleCounts.user || 0,
            svgColor: 'var(--bs-primary)',
            bgClass: 'bg-primary',
        },
        {
            label: 'Researcher',
            count: roleCounts.researcher || 0,
            svgColor: 'var(--bs-success)',
            bgClass: 'bg-success',
        },
        {
            label: 'Admin',
            count: roleCounts.admin || 0,
            svgColor: 'var(--bs-danger)',
            bgClass: 'bg-danger',
        },
    ];

    function polarToCartesian(angle) {
        const rad = ((angle - 90) * Math.PI) / 180;
        return {
            x: cx + r * Math.cos(rad),
            y: cy + r * Math.sin(rad),
        };
    }

    function slicePath(startAngle, endAngle) {
        const start = polarToCartesian(startAngle);
        const end = polarToCartesian(endAngle);
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
    }

    let currentAngle = 0;
    const slices = roles.map((role) => {
        const pct = total > 0 ? role.count / total : 0;
        const sweep = pct * 360;
        const path = slicePath(currentAngle, currentAngle + sweep);
        const midAngle = currentAngle + sweep / 2;
        const midRad = ((midAngle - 90) * Math.PI) / 180;
        const labelR = r * 0.65;
        const lx = cx + labelR * Math.cos(midRad);
        const ly = cy + labelR * Math.sin(midRad);
        currentAngle += sweep;
        return {
            ...role,
            pct,
            path,
            lx,
            ly,
            color: role.svgColor,
            bgClass: role.bgClass,
        };
    });

    return (
        <div className="d-flex align-items-center gap-4 flex-wrap">
            <svg
                viewBox="0 0 240 260"
                role="img"
                aria-label="Role distribution pie chart"
                className="admin-pie-chart-svg"
            >
                {slices.map((slice) => (
                    <path
                        key={slice.label}
                        d={slice.path}
                        fill={slice.color}
                        className="admin-pie-slice"
                        strokeWidth="2"
                    />
                ))}

                {/* Percentage labels on larger slices */}
                {slices.map(
                    (slice) =>
                        slice.pct > 0.08 && (
                            <text
                                key={`label-${slice.label}`}
                                x={slice.lx}
                                y={slice.ly}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="16"
                                fontWeight="600"
                                className="admin-pie-slice-label"
                            >
                                {Math.round(slice.pct * 100)}%
                            </text>
                        )
                )}
            </svg>

            {/* Legend */}
            <div className="d-flex flex-column gap-2">
                {slices.map((slice) => (
                    <div
                        key={slice.label}
                        className="d-flex align-items-center gap-2"
                    >
                        <span
                            className={`rounded-circle d-inline-block admin-legend-dot ${slice.bgClass}`}
                            aria-hidden="true"
                        />
                        <span className="small">
                            {slice.label}{' '}
                            <span className="text-secondary">
                                ({Math.round(slice.pct * 100)}%)
                            </span>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

RoleDistributionChart.propTypes = {
    roleCounts: PropTypes.shape({
        user: PropTypes.number,
        researcher: PropTypes.number,
        admin: PropTypes.number,
    }).isRequired,
    total: PropTypes.number.isRequired,
};

// --- Stat Card ---
function StatCard({ iconClass, label, value, subtitle, valueClass = '' }) {
    return (
        <Card className="h-100 shadow-sm">
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="text-secondary small">{label}</span>
                    <i className={`bi ${iconClass} fs-5`} aria-hidden="true" />
                </div>
                <div className={`fs-3 fw-bold mb-1 ${valueClass || ''}`}>
                    {value}
                </div>
                <div className="text-secondary small">{subtitle}</div>
            </Card.Body>
        </Card>
    );
}

StatCard.propTypes = {
    iconClass: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    valueClass: PropTypes.string,
};

// --- Main Overview Component ---
export default function OverviewAdmin() {
    const api = useAuthenticatedAxios();
    const [users, setUsers] = useState([]);
    const [systemStatus, setSystemStatus] = useState(null);
    const [pendingReports, setPendingReports] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [usersRes, healthRes, reportsRes] = await Promise.all([
                api.get('/api/users'),
                api
                    .get('/health')
                    .catch(() => ({ data: { status: 'Unknown' } })),
                getReportedPosts(api).catch(() => []),
            ]);
            setUsers(usersRes.data);
            setSystemStatus(healthRes.data?.status || 'Unknown');
            setPendingReports(
                Array.isArray(reportsRes) ? reportsRes.length : 0
            );
        } catch (err) {
            console.error('Failed to load overview data:', err);
            setError('Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" variant="light" />
                <span className="visually-hidden">Loading overview...</span>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    const now = new Date();
    const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    const totalUsers = users.length;
    // TODO: use real lastLogin field when available; using updatedAt as proxy
    const activeUsers = users.filter(
        (u) => u.updatedAt && new Date(u.updatedAt) > thirtyDaysAgo
    ).length;
    const newUsers = users.filter(
        (u) => u.createdAt && new Date(u.createdAt) > sevenDaysAgo
    ).length;

    const roleCounts = users.reduce((acc, u) => {
        const role = u.role || 'user';
        acc[role] = (acc[role] || 0) + 1;
        return acc;
    }, {});

    const isHealthy =
        systemStatus?.toLowerCase() === 'ok' ||
        systemStatus?.toLowerCase() === 'healthy';

    return (
        <Container className="py-4">
            <SEO title="Admin: Overview" />
            {/* Stat cards */}
            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} lg>
                    <StatCard
                        iconClass="bi-people text-secondary"
                        label="Total Users"
                        value={totalUsers.toLocaleString()}
                        subtitle="Total registered accounts"
                    />
                </Col>
                <Col xs={12} sm={6} lg>
                    <StatCard
                        iconClass="bi-person-check text-secondary"
                        label="Active Users"
                        value={activeUsers.toLocaleString()}
                        subtitle="Active in last 30 days"
                    />
                </Col>
                <Col xs={12} sm={6} lg>
                    <StatCard
                        iconClass="bi-person-add text-secondary"
                        label="New Users (7d)"
                        value={newUsers.toLocaleString()}
                        subtitle="Registered this week"
                    />
                </Col>
                <Col xs={12} sm={6} lg>
                    <StatCard
                        iconClass={
                            pendingReports > 0
                                ? 'bi-flag-fill text-danger'
                                : 'bi-flag text-secondary'
                        }
                        label="Pending Reports"
                        value={pendingReports.toLocaleString()}
                        subtitle="Awaiting moderation"
                        valueClass={pendingReports > 0 ? 'text-danger' : ''}
                    />
                </Col>
                <Col xs={12} sm={6} lg>
                    <StatCard
                        iconClass={
                            isHealthy
                                ? 'bi-check-circle text-success'
                                : 'bi-exclamation-circle text-warning'
                        }
                        label="System Status"
                        value={isHealthy ? 'Healthy' : systemStatus}
                        subtitle="All services operational"
                        valueClass={isHealthy ? 'text-success' : 'text-warning'}
                    />
                </Col>
            </Row>

            {/* Charts */}
            <Row className="g-3">
                <Col xs={12} lg={7}>
                    <Card className="shadow-sm h-100">
                        <Card.Body className="p-3">
                            <h2 className="fs-6 fw-semibold mb-3">
                                User Growth
                            </h2>
                            {/* TODO: Replace MOCK_MONTHLY_DATA with real historical data from a dedicated endpoint */}
                            <UserGrowthChart data={MOCK_MONTHLY_DATA} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} lg={5}>
                    <Card className="shadow-sm h-100">
                        <Card.Body className="p-3">
                            <h2 className="fs-6 fw-semibold mb-3">
                                Role Distribution
                            </h2>
                            <RoleDistributionChart
                                roleCounts={roleCounts}
                                total={totalUsers}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
